import { logDataModels } from '../dataModelsLogger';
import { evaluateBoolean, parseExpression, validateExpression } from './parser';
import { evaluationContext } from './queryTest';
import type { PolicyAsset, SyntheticUser } from './types';

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

const SQL_PATTERN = /^\s*SELECT\s+([\s\S]+?)\s+FROM\s+([A-Za-z_][\w.]*)(?:\s+WHERE\s+([\s\S]+?))?(?:\s+LIMIT\s+(\d+))?\s*;?\s*$/i;

export interface CatalogQueryResult {
  ok: boolean;
  errors: string[];
  columns: string[];
  rows: Array<Record<string, unknown>>;
  matched: number;
  scanned: number;
  truncated: boolean;
  assetName?: string;
  where: string;
  explanation: string;
  unsupported?: boolean;
}

export interface ParsedCatalogSql {
  select: string[] | '*';
  from: string;
  where: string;
  limit: number;
}

function fail(errors: string[], explanation: string, extra: Partial<CatalogQueryResult> = {}): CatalogQueryResult {
  return {
    ok: false,
    errors,
    columns: [],
    rows: [],
    matched: 0,
    scanned: 0,
    truncated: false,
    where: extra.where ?? '',
    explanation,
    ...extra,
  };
}

export function looksLikeSql(source: string): boolean {
  return /^\s*SELECT\b/i.test(source);
}

export function parseCatalogSql(source: string): { parsed?: ParsedCatalogSql; errors: string[] } {
  const trimmed = source.trim().replace(/;+\s*$/, '');
  if (!trimmed) return { errors: ['Write a query first.'] };
  const match = trimmed.match(SQL_PATTERN);
  if (!match) {
    return {
      errors: ['Use SELECT columns FROM table [WHERE filter] [LIMIT n]. The simulator runs this against sample catalog rows.'],
    };
  }
  const selectRaw = match[1].trim();
  const from = match[2].trim();
  const where = (match[3] ?? '').trim();
  const limitRaw = match[4] ? Number(match[4]) : DEFAULT_LIMIT;
  if (!from) return { errors: ['FROM requires a table name.'] };
  if (!Number.isFinite(limitRaw) || limitRaw < 1) return { errors: ['LIMIT must be a positive integer.'] };
  const limit = Math.min(Math.floor(limitRaw), MAX_LIMIT);
  if (selectRaw === '*') {
    return { parsed: { select: '*', from, where, limit }, errors: [] };
  }
  const select = selectRaw.split(',').map((part) => part.trim()).filter(Boolean);
  if (!select.length) return { errors: ['SELECT requires * or at least one column.'] };
  if (select.some((column) => !/^[A-Za-z_][\w.]*$/.test(column))) {
    return { errors: ['SELECT columns must be identifiers. Aliases and expressions are not supported here.'] };
  }
  return { parsed: { select, from, where, limit }, errors: [] };
}

export function extractWhereClause(source: string): string {
  const trimmed = source.trim();
  if (!trimmed) return '';
  if (!looksLikeSql(trimmed)) return trimmed;
  const { parsed } = parseCatalogSql(trimmed);
  return parsed?.where ?? '';
}

export function seedCatalogQuery(expression: string, assets: PolicyAsset[]): string {
  const table = assets[0]?.name ?? 'asset';
  const trimmed = expression.trim();
  if (!trimmed) return `SELECT * FROM ${table} LIMIT ${DEFAULT_LIMIT}`;
  if (looksLikeSql(trimmed)) return trimmed;
  return `SELECT * FROM ${table} WHERE ${trimmed}`;
}

function resolveAsset(name: string, assets: PolicyAsset[]): PolicyAsset | undefined {
  const needle = name.toLowerCase();
  return assets.find((asset) => asset.name.toLowerCase() === needle || asset.id.toLowerCase() === needle);
}

function projectRow(row: Record<string, unknown>, columns: string[]): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  columns.forEach((column) => {
    next[column] = row[column];
  });
  return next;
}

export function runCatalogQuery(
  source: string,
  assets: PolicyAsset[],
  user: SyntheticUser,
): CatalogQueryResult {
  const { parsed, errors } = parseCatalogSql(source);
  if (!parsed) {
    logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'parse' });
    return fail(errors, errors[0] ?? 'The query did not parse.');
  }

  const asset = resolveAsset(parsed.from, assets);
  if (!asset) {
    const available = assets.map((item) => item.name).join(', ') || 'none in this scope';
    const message = `Unknown table '${parsed.from}'. Available: ${available}.`;
    logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'unknown_table', from: parsed.from });
    return fail([message], message, { where: parsed.where });
  }

  const columns = parsed.select === '*' ? asset.columns.map((column) => column.name) : parsed.select;
  const unknown = columns.filter((column) => !asset.columns.some((item) => item.name === column));
  if (unknown.length) {
    const message = `Unknown column${unknown.length === 1 ? '' : 's'} ${unknown.map((item) => `'${item}'`).join(', ')} on ${asset.name}.`;
    logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'unknown_column', columns: unknown });
    return fail([message], message, { assetName: asset.name, where: parsed.where });
  }

  if (parsed.where) {
    const validation = validateExpression(parsed.where, [asset]);
    if (!validation.ok) {
      const explanation = validation.unsupported
        ? 'This filter uses something the frontend simulator cannot run.'
        : 'The WHERE clause did not parse.';
      logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'where', unsupported: Boolean(validation.unsupported) });
      return fail(validation.errors, explanation, { assetName: asset.name, where: parsed.where, unsupported: validation.unsupported });
    }
  }

  const parsedWhere = parsed.where ? parseExpression(parsed.where) : { ast: undefined, issues: [] };
  if (parsed.where && !parsedWhere.ast) {
    const messages = parsedWhere.issues.map((issue) => issue.message);
    logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'where_ast' });
    return fail(messages, 'The WHERE clause did not parse.', { assetName: asset.name, where: parsed.where });
  }

  const matched: Array<Record<string, unknown>> = [];
  for (const row of asset.rows) {
    if (parsedWhere.ast) {
      const value = evaluateBoolean(parsedWhere.ast, evaluationContext(asset, user, row));
      if (value === 'unsupported') {
        const explanation = 'The query is syntactically valid but this operator cannot be evaluated here.';
        logDataModels('warning', 'governance_query_builder_ran', { ok: false, reason: 'unsupported', table: asset.name });
        return fail([], explanation, { assetName: asset.name, where: parsed.where, unsupported: true });
      }
      if (!value) continue;
    }
    matched.push(projectRow(row, columns));
  }

  const truncated = matched.length > parsed.limit;
  const rows = truncated ? matched.slice(0, parsed.limit) : matched;
  const explanation = parsed.where
    ? `${rows.length} of ${asset.rows.length} sample rows match for ${user.name} on ${asset.name}.`
    : `${rows.length} sample rows from ${asset.name} as ${user.name}.`;

  logDataModels('app', 'governance_query_builder_ran', {
    ok: true,
    table: asset.name,
    matched: matched.length,
    returned: rows.length,
    scanned: asset.rows.length,
    userId: user.id,
  });

  return {
    ok: true,
    errors: [],
    columns,
    rows,
    matched: matched.length,
    scanned: asset.rows.length,
    truncated,
    assetName: asset.name,
    where: parsed.where,
    explanation,
  };
}
